// ════════════════════════════════════════════════════════════════════════════
//  useLatido.js  ·  El pulso de las pantallas que se refrescan solas
//
//  ── POR QUÉ EXISTE (19-09-2026) ────────────────────────────────────────────
//  Neon avisó a Rob de que el proyecto iba por el 84 % de su transferencia
//  mensual (4,2 GB de 5 GB) y él seguía trabajando. Al medirlo, el grueso no
//  eran las consultas pesadas —una lista del CRM son 4 KB—, sino el GOTEO: el
//  Live Chat de gerencia pregunta por las conversaciones cada 3,5 segundos y
//  por los mensajes cada 2,5, y el chat del alumno cada 3. Sin parar, mientras
//  la pestaña exista.
//
//  Medido: la lista de conversaciones son 5,3 KB · 1.029 rondas por hora =
//  5,3 MB/hora. Con el panel abierto 8 horas al día son 0,92 GB al mes, de una
//  sola persona y de una sola de las tres consultas. Y eso ANTES de contar a
//  los alumnos, que crecen con el negocio.
//
//  Lo caro no es preguntar: es preguntar cuando no hay nadie mirando.
//
//  Este latido hace dos cosas, las dos invisibles para quien usa la app:
//
//    1. CALLA cuando la pestaña no se ve (minimizada, en otra pestaña, móvil
//       en el bolsillo) y vuelve a preguntar EN EL ACTO al volver. Si no la
//       estás mirando, refrescarla no te sirve de nada.
//    2. AFLOJA el ritmo cuando no pasa nada: del ritmo normal va doblando
//       hasta un techo, y en cuanto hay novedad —o quien mira escribe algo—
//       vuelve al ritmo rápido de golpe. Una conversación viva sigue yendo a
//       2,5 segundos; una pestaña abierta desde ayer pregunta cada medio
//       minuto.
//
//  Lo que NO hace: perder mensajes. Al volver a mirar se pregunta de
//  inmediato, y cualquier novedad devuelve el ritmo rápido.
// ════════════════════════════════════════════════════════════════════════════
import { useEffect, useRef, useCallback } from "react";

/** ¿Está la pestaña a la vista? En un navegador raro, se asume que sí. */
function seVe() {
    try {
        return document.visibilityState !== "hidden";
    } catch {
        return true;
    }
}

/**
 * Llama a `tarea` cada `base` ms mientras la pantalla se vea.
 *
 * `tarea` puede devolver `true` cuando hubo novedad (mensajes nuevos, cambios):
 * eso devuelve el latido a su ritmo rápido. Devolver `false` o nada lo deja
 * aflojar hasta `techo`.
 *
 * Devuelve `acelera()`, para llamarlo cuando quien mira hace algo (escribir,
 * abrir una conversación): el ritmo vuelve a `base` y se pregunta ya.
 */
export function useLatido(tarea, { base = 3000, techo = 30000, activo = true } = {}) {
    const tareaRef = useRef(tarea);
    const temporizador = useRef(null);
    const ritmo = useRef(base);
    const corriendo = useRef(false);

    useEffect(() => { tareaRef.current = tarea; }, [tarea]);
    useEffect(() => { ritmo.current = base; }, [base]);

    const para = useCallback(() => {
        if (temporizador.current) {
            clearTimeout(temporizador.current);
            temporizador.current = null;
        }
    }, []);

    // Un setTimeout encadenado, no un setInterval: así el ritmo puede cambiar
    // entre una vuelta y la siguiente sin recrear nada.
    const vuelta = useCallback(async (yaMismo = false) => {
        para();
        if (!activo || !seVe()) return;
        if (!yaMismo && corriendo.current) return;
        corriendo.current = true;
        let hubo = false;
        try {
            hubo = await tareaRef.current();
        } catch {
            // Un fallo no acelera: si el servidor está caído, insistir cada
            // 2,5 segundos no lo arregla y multiplica el tráfico.
        } finally {
            corriendo.current = false;
        }
        ritmo.current = hubo ? base : Math.min(ritmo.current * 2, techo);
        if (activo && seVe()) {
            temporizador.current = setTimeout(() => vuelta(), ritmo.current);
        }
    }, [activo, base, techo, para]);

    const acelera = useCallback(() => {
        ritmo.current = base;
        vuelta(true);
    }, [base, vuelta]);

    useEffect(() => {
        if (!activo) { para(); return undefined; }
        ritmo.current = base;
        vuelta(true);

        const alCambiarVisibilidad = () => {
            if (seVe()) {
                // Al volver: ritmo rápido y una pregunta inmediata, para que
                // quien vuelve no vea una pantalla vieja ni un segundo.
                ritmo.current = base;
                vuelta(true);
            } else {
                para();
            }
        };
        document.addEventListener("visibilitychange", alCambiarVisibilidad);
        return () => {
            document.removeEventListener("visibilitychange", alCambiarVisibilidad);
            para();
        };
    }, [activo, base, vuelta, para]);

    return acelera;
}

export default useLatido;
